import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, RefreshCw, Loader2, Gift, CreditCard, Ban, CheckCircle2, Trash2,
  ClipboardList, X, ChevronLeft, ChevronRight, Filter, ArrowUpDown, User as UserIcon,
  AlertTriangle, ExternalLink, Download, BadgeCheck, ShieldAlert, Chrome, Mail,
} from "lucide-react";
import { adminAxios, formatINR, relativeTime, PlanBadge, PLAN_OPTIONS } from "./adminApi";
import UserDetailDrawer from "./UserDetailDrawer";

const SORTS = [
  { id: "signup", label: "Signup date" },
  { id: "last_active", label: "Last active" },
  { id: "scans_today", label: "Scans today" },
  { id: "revenue", label: "Revenue" },
];

function Tooltip({ label, children }) {
  return (
    <span className="relative group inline-flex">
      {children}
      <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-8 px-2 py-1 rounded bg-lm-text text-white text-[10px] font-mono whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-30">
        {label}
      </span>
    </span>
  );
}

function IconBtn({ icon: Ico, onClick, label, testId, danger }) {
  return (
    <Tooltip label={label}>
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        aria-label={label}
        className={`p-1.5 rounded transition-colors ${
          danger ? "text-red-600 hover:bg-red-50" : "text-lm-text2 hover:text-lm-text hover:bg-lm-tint"
        }`}
      >
        <Ico className="w-3.5 h-3.5" />
      </button>
    </Tooltip>
  );
}

function Avatar({ user }) {
  if (user.picture) {
    return <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />;
  }
  const initial = (user.name || user.email || "?").charAt(0).toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full bg-lm-primary text-white font-display font-semibold text-sm flex items-center justify-center shrink-0">
      {initial}
    </div>
  );
}

export default function AdminUsers() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [q, setQ] = useState(params.get("q") || "");
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [drawer, setDrawer] = useState(null);          // user_id
  const [modal, setModal] = useState(null);            // {type, user}
  const [exporting, setExporting] = useState(false);

  // useMemo, not a bare object literal: `load` below depends on `filters`, and a
  // new object every render would give `load` a new identity every render, which
  // `useEffect(() => load(), [load])` would turn into a fetch on every render —
  // an endless request loop. Memoised on `params`, the real input.
  const filters = useMemo(() => ({
    q: params.get("q") || "",
    plan: params.get("plan") || "",
    status: params.get("status") || "",
    category: params.get("category") || "",
    sort: params.get("sort") || "signup",
    page: parseInt(params.get("page") || "1", 10),
  }), [params]);

  const updateParams = useCallback((patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) next.delete(k);
      else next.set(k, String(v));
    });
    // Any change other than `page` resets pagination
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAxios().get("/users", { params: { ...filters, page_size: 20 } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    setExporting(true);
    try {
      // Reuse the same filters as the current view.
      const r = await adminAxios().get("/users/export.csv", {
        params: {
          q: filters.q || undefined,
          plan: filters.plan || undefined,
          status: filters.status || undefined,
          category: filters.category || undefined,
          sort: filters.sort || undefined,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "").replace(/-/g, "");
      a.download = `leadmato_users_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Users CSV downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV export failed");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    adminAxios().get("/categories").then((r) => setCategories(r.data.items || [])).catch(() => {});
  }, []);

  const items = data.items || [];

  return (
    <div data-testid="admin-users">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-lm-secondary">People</div>
        <h1 className="mt-1 text-2xl md:text-3xl font-display font-semibold tracking-tight text-lm-text">Users</h1>
        <p className="text-sm text-lm-text2 mt-1">{data.total} total · filter, sort, drill into any user.</p>
      </div>

      {/* Filter bar */}
      <div className="bg-lm-surface border border-lm-border rounded-lg p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="users-filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-lm-text3" />
          <input
            data-testid="users-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && updateParams({ q })}
            placeholder="Search by email or name…"
            className="w-full pl-9 pr-3 py-2 rounded-md border border-lm-border bg-lm-bg text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20"
          />
        </div>
        <select
          data-testid="users-plan-filter"
          value={filters.plan}
          onChange={(e) => updateParams({ plan: e.target.value })}
          className="text-sm border border-lm-border rounded-md px-3 py-2 bg-lm-bg"
        >
          <option value="">All plans</option>
          <option value="paid">All paid</option>
          {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          data-testid="users-status-filter"
          value={filters.status}
          onChange={(e) => updateParams({ status: e.target.value })}
          className="text-sm border border-lm-border rounded-md px-3 py-2 bg-lm-bg"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <select
          data-testid="users-category-filter"
          value={filters.category}
          onChange={(e) => updateParams({ category: e.target.value })}
          className="text-sm border border-lm-border rounded-md px-3 py-2 bg-lm-bg max-w-[200px]"
        >
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select
          data-testid="users-sort"
          value={filters.sort}
          onChange={(e) => updateParams({ sort: e.target.value })}
          className="text-sm border border-lm-border rounded-md px-3 py-2 bg-lm-bg"
        >
          {SORTS.map((s) => <option key={s.id} value={s.id}>Sort: {s.label}</option>)}
        </select>
        <button
          onClick={load}
          data-testid="users-refresh"
          className="inline-flex items-center gap-1.5 text-xs bg-lm-tint text-lm-text2 border border-lm-border rounded-md px-3 py-2 hover:bg-lm-tint2"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
        <button
          onClick={downloadCsv}
          disabled={exporting}
          data-testid="users-export-csv"
          title="Download filtered users as CSV"
          className="inline-flex items-center gap-1.5 text-xs bg-lm-primary text-white border border-lm-primary rounded-md px-3 py-2 hover:bg-lm-primaryHover disabled:opacity-60"
        >
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Export CSV
        </button>
        {(filters.q || filters.plan || filters.status || filters.category) && (
          <button
            onClick={() => { setQ(""); setParams(new URLSearchParams(), { replace: true }); }}
            data-testid="users-clear-filters"
            className="text-xs text-lm-text2 hover:text-lm-text inline-flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-lm-surface border border-lm-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto md:overflow-x-clip">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col className="w-[20%]" />
              <col className="w-[7%]" />
              <col className="w-[12%]" />
              <col className="w-[6%]" />
              <col className="w-[6%]" />
              <col className="w-[6%]" />
              <col className="w-[6%]" />
              <col className="w-[7%]" />
              <col className="w-[7%]" />
              <col className="w-[7%]" />
              <col className="w-[16%]" />
            </colgroup>
            <thead className="bg-lm-tint/60 border-b border-lm-border text-lm-text3">
              <tr>
                <th className="text-left px-3 py-2.5 font-medium">User</th>
                <th className="text-left px-2 py-2.5 font-medium">Plan</th>
                <th className="text-left px-3 py-2.5 font-medium">Category</th>
                <th className="text-left px-2 py-2.5 font-medium">Verified</th>
                <th className="text-left px-2 py-2.5 font-medium">Signed via</th>
                <th className="text-right px-2 py-2.5 font-medium">Today</th>
                <th className="text-right px-2 py-2.5 font-medium">Total</th>
                <th className="text-right px-2 py-2.5 font-medium">Signup</th>
                <th className="text-right px-2 py-2.5 font-medium">Active</th>
                <th className="text-right px-2 py-2.5 font-medium">Status</th>
                <th className="text-right px-2 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-lm-text2">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading users…
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-lm-text3">No users match these filters.</td></tr>
              ) : items.map((u) => (
                <tr
                  key={u.user_id}
                  data-testid={`user-row-${u.user_id}`}
                  onClick={() => setDrawer(u.user_id)}
                  className="border-t border-lm-border/60 hover:bg-lm-tint/30 cursor-pointer transition-colors"
                >
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Avatar user={u} />
                      <div className="min-w-0">
                        <div className="font-medium truncate">{u.email}</div>
                        <div className="text-[11px] text-lm-text3 font-mono truncate">{u.name || u.user_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2.5"><PlanBadge plan={u.plan} /></td>
                  <td className="px-3 py-2.5">
                    <div className="text-xs text-lm-text truncate">{u.business_category_label || u.business_category || "—"}</div>
                    {u.market_scope && (
                      <div className="text-[10px] font-mono text-lm-text3 uppercase tracking-widest truncate">
                        {u.market_scope}{u.market_city ? ` · ${u.market_city}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2.5">
                    {u.email_verified ? (
                      <span
                        data-testid={`user-verified-${u.user_id}`}
                        title="Email verified via OTP or Google"
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                      >
                        <BadgeCheck className="w-3 h-3" /> Verified
                      </span>
                    ) : (
                      <span
                        data-testid={`user-unverified-${u.user_id}`}
                        title="Email not verified — user did not complete OTP during signup"
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                      >
                        <ShieldAlert className="w-3 h-3" /> Pending
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2.5">
                    {(() => {
                      const methods = u.auth_methods || [];
                      const isGoogle = methods.includes("google");
                      const isEmail = methods.includes("email");
                      const label = isGoogle && isEmail ? "Both" : isGoogle ? "Google" : "Email";
                      const Icon = isGoogle ? Chrome : Mail;
                      const cls = isGoogle
                        ? "bg-blue-50 text-blue-700 border-blue-200"
                        : "bg-lm-tint2 text-lm-primary border-lm-tint2";
                      return (
                        <span
                          data-testid={`user-auth-${u.user_id}`}
                          title={`Signed up via ${label}${methods.length > 1 ? ` (used ${methods.join(" + ")})` : ""}`}
                          className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${cls}`}
                        >
                          <Icon className="w-3 h-3" /> {label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono">{u.scans_today}</td>
                  <td className="px-2 py-2.5 text-right font-mono">{u.scans_total}</td>
                  <td className="px-2 py-2.5 text-right text-xs text-lm-text3 font-mono whitespace-nowrap">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-2 py-2.5 text-right text-xs text-lm-text3 font-mono whitespace-nowrap">{relativeTime(u.last_active_at)}</td>
                  <td className="px-2 py-2.5 text-right">
                    {u.suspended
                      ? <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">Suspended</span>
                      : <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>}
                  </td>
                  <td className="px-2 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-0">
                      <IconBtn icon={RefreshCw} label="Refill today's scan limit to max" onClick={() => setModal({ type: "reset", user: u })} testId={`action-reset-${u.user_id}`} />
                      <IconBtn icon={Gift} label="Add bonus scans to this user" onClick={() => setModal({ type: "grant", user: u })} testId={`action-grant-${u.user_id}`} />
                      <IconBtn icon={ClipboardList} label="See full credit transaction history" onClick={() => setModal({ type: "ledger", user: u })} testId={`action-ledger-${u.user_id}`} />
                      <IconBtn icon={CreditCard} label="Upgrade or downgrade plan manually" onClick={() => setModal({ type: "plan", user: u })} testId={`action-plan-${u.user_id}`} />
                      <IconBtn
                        icon={u.suspended ? CheckCircle2 : Ban}
                        label={u.suspended ? "Unblock — allow login again" : "Block user from logging in"}
                        onClick={() => setModal({ type: "suspend", user: u })}
                        testId={`action-suspend-${u.user_id}`}
                        danger={!u.suspended}
                      />
                      <IconBtn icon={Trash2} label="Permanently delete this user and all their data" onClick={() => setModal({ type: "delete", user: u })} testId={`action-delete-${u.user_id}`} danger />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-lm-border text-xs text-lm-text3 font-mono">
          <div>
            Page {data.page} of {data.total_pages} · {data.total} users
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="users-prev-page"
              disabled={data.page <= 1}
              onClick={() => updateParams({ page: data.page - 1 })}
              className="p-1.5 rounded border border-lm-border hover:bg-lm-tint disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid="users-next-page"
              disabled={data.page >= data.total_pages}
              onClick={() => updateParams({ page: data.page + 1 })}
              className="p-1.5 rounded border border-lm-border hover:bg-lm-tint disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {drawer && <UserDetailDrawer userId={drawer} onClose={() => setDrawer(null)} onAction={(t, u) => setModal({ type: t, user: u })} />}
      {modal && <ActionModal modal={modal} onClose={() => setModal(null)} onDone={load} />}
    </div>
  );
}


// ============== Modal ==============
function ActionModal({ modal, onClose, onDone }) {
  const { user, type } = modal;
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState(10);
  const [plan, setPlan] = useState(user.plan || "free");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [ledger, setLedger] = useState(null);

  useEffect(() => {
    if (type !== "ledger") return;
    (async () => {
      try {
        const r = await adminAxios().get(`/users/${user.user_id}/credit-ledger?workspace_id=${user.workspace_id}&limit=50`);
        setLedger(r.data.items);
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    })();
  }, [type, user]);

  const doReset = async () => {
    setBusy(true);
    try {
      const r = await adminAxios().post(`/users/${user.user_id}/reset-quota`, { workspace_id: user.workspace_id });
      toast.success(`Quota reset · cleared ${r.data.deleted} action logs`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doGrant = async () => {
    setBusy(true);
    try {
      const r = await adminAxios().post(`/users/${user.user_id}/grant-scans`, { workspace_id: user.workspace_id, amount });
      toast.success(`Granted · new balance ${r.data.topup_scans_balance}`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doPlan = async () => {
    setBusy(true);
    try {
      await adminAxios().post(`/users/${user.user_id}/change-plan`, { workspace_id: user.workspace_id, plan });
      toast.success(`Plan → ${plan}`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doSuspend = async () => {
    setBusy(true);
    try {
      await adminAxios().post(`/users/${user.user_id}/suspend`, { suspended: !user.suspended });
      toast.success(user.suspended ? "Unsuspended" : "Suspended");
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await adminAxios().post(`/users/${user.user_id}/delete`, { confirm_email: confirmEmail });
      const total = Object.values(r.data.deleted || {}).reduce((a, v) => a + (typeof v === "number" ? v : 0), 0);
      toast.success(`User deleted · ${total} records removed across ${Object.keys(r.data.deleted || {}).length} collections`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  const titles = {
    reset: "Reset today's quota",
    grant: "Grant bonus scans",
    plan: "Change plan manually",
    ledger: "Credit ledger",
    suspend: user.suspended ? "Unsuspend user" : "Suspend user",
    delete: "Delete user account",
  };

  const confirmMatches = confirmEmail.trim().toLowerCase() === (user.email || "").trim().toLowerCase();

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4 z-50" data-testid={`modal-${type}`}>
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-lm-border">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3 truncate">{user.email}</div>
            <h3 className="font-display font-semibold">{titles[type]}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-lm-tint rounded"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5">
          {type === "reset" && (
            <>
              <p className="text-sm text-lm-text2">Refills today's scan limit to maximum — clears today's action log entries and resets the daily usage counter for this workspace.</p>
              <button onClick={doReset} disabled={busy} data-testid="modal-reset-submit" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-lm-primary text-white rounded-lg px-5 py-2.5 font-semibold hover:bg-lm-primaryHover disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Reset quota
              </button>
            </>
          )}
          {type === "grant" && (
            <>
              <label className="block text-xs font-mono uppercase tracking-widest text-lm-text3 mb-1.5">Scans to add</label>
              <input
                type="number" min="1" max="10000"
                data-testid="modal-grant-amount"
                value={amount}
                onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
                className="w-full border border-lm-border rounded-lg px-4 py-2.5"
              />
              <p className="text-xs text-lm-text3 mt-2">Added to the top-up bucket · never expires · logged to credit ledger.</p>
              <button onClick={doGrant} disabled={busy || amount < 1} data-testid="modal-grant-submit" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-lm-primary text-white rounded-lg px-5 py-2.5 font-semibold hover:bg-lm-primaryHover disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} Grant {amount} scans
              </button>
            </>
          )}
          {type === "plan" && (
            <>
              <label className="block text-xs font-mono uppercase tracking-widest text-lm-text3 mb-1.5">New plan</label>
              <select data-testid="modal-plan-select" value={plan} onChange={(e) => setPlan(e.target.value)} className="w-full border border-lm-border rounded-lg px-4 py-2.5 bg-white">
                {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <p className="text-xs text-lm-text3 mt-2">Sets `plan` + `subscription_key`. Does not touch Razorpay — manual override only.</p>
              <button onClick={doPlan} disabled={busy} data-testid="modal-plan-submit" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-lm-primary text-white rounded-lg px-5 py-2.5 font-semibold disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Apply
              </button>
            </>
          )}
          {type === "suspend" && (
            <>
              <p className="text-sm text-lm-text2">
                {user.suspended
                  ? "Restore login access for this user. They can immediately sign in again."
                  : "Block this user from logging in. Their active sessions will be revoked immediately."}
              </p>
              <button onClick={doSuspend} disabled={busy} data-testid="modal-suspend-submit" className={`mt-4 w-full inline-flex items-center justify-center gap-2 text-white rounded-lg px-5 py-2.5 font-semibold disabled:opacity-60 ${user.suspended ? "bg-lm-primary hover:bg-lm-primaryHover" : "bg-red-600 hover:bg-red-700"}`}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : user.suspended ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                {user.suspended ? "Unsuspend" : "Suspend user"}
              </button>
            </>
          )}
          {type === "delete" && (
            <>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 border border-red-200 mb-4">
                <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <div className="text-xs text-red-800">
                  Permanently deletes this user and every record they own — workspaces, brands, scans, leads, credit transactions,
                  API keys, invites, waitlist entries. <b>This cannot be undone.</b>
                </div>
              </div>
              <label className="block text-xs font-mono uppercase tracking-widest text-lm-text3 mb-1.5">
                Type <span className="text-red-700 normal-case font-sans">{user.email}</span> to confirm
              </label>
              <input
                type="email" autoComplete="off" data-testid="modal-delete-confirm"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder={user.email}
                className="w-full border border-lm-border rounded-lg px-4 py-2.5 font-mono text-sm"
              />
              <button onClick={doDelete} disabled={busy || !confirmMatches} data-testid="modal-delete-submit" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white rounded-lg px-5 py-2.5 font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Permanently delete
              </button>
            </>
          )}
          {type === "ledger" && (
            <div className="max-h-96 overflow-y-auto -mx-5">
              {ledger === null ? (
                <div className="p-4 text-sm text-lm-text2"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
              ) : ledger.length === 0 ? (
                <div className="p-4 text-sm text-lm-text3">No credit transactions.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-lm-tint/60 text-lm-text3 sticky top-0">
                    <tr>
                      <th className="text-left px-5 py-2">When</th>
                      <th className="text-left px-5 py-2">Reason</th>
                      <th className="text-right px-5 py-2">Δ</th>
                      <th className="text-right px-5 py-2">Bal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((it) => (
                      <tr key={it.tx_id} className="border-t border-lm-border/60">
                        <td className="px-5 py-1.5 font-mono">{it.created_at ? new Date(it.created_at).toLocaleString() : ""}</td>
                        <td className="px-5 py-1.5">{it.reason}</td>
                        <td className={`px-5 py-1.5 text-right font-mono font-semibold ${it.delta > 0 ? "text-emerald-700" : "text-lm-text"}`}>{it.delta > 0 ? "+" : ""}{it.delta}</td>
                        <td className="px-5 py-1.5 text-right font-mono">{it.balance_after ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
